'use client'

import useResizeObserver from '@/hooks/useResizeObserver'
import { Box } from '@/styled-system/jsx'
import { type HTMLStyledProps } from '@/styled-system/types'
import { memo, useRef } from 'react'
import MinesTile, { MinesTileProps } from './tile'

export type MinesTilesProps = HTMLStyledProps<'div'> & {
  tiles: MinesTileProps[]
  onClickTile?: (index: number) => void
}

const MinesTiles = memo(function MinesTiles({
  tiles,
  onClickTile,
  ...props
}: MinesTilesProps) {
  const ref = useRef<HTMLDivElement>(null)

  useResizeObserver(ref, (entries) => {
    // Only update if width changed by at least 5px to reduce unnecessary recalculations
    const { width } = entries[0].contentRect
    const target = entries[0].target as HTMLElement
    const currentSize = parseFloat(target.style.fontSize || '0')
    const newSize = width / 630

    // Only update if the change is significant (more than 2%)
    if (Math.abs(currentSize - newSize) / newSize > 0.02) {
      target.style.fontSize = `${newSize}em`
    }
  }, { box: 'border-box' })

  return (
    <Box
      ref={ref}
      position='relative'
      display='grid'
      gridTemplateColumns='repeat(5, auto)'
      columnGap='0.75em'
      rowGap='1em'
      p='0.75em'
      w='full'
      maxW='630px'
      {...props}
    >
      {tiles.map((tile, index) => {
        return (
          <MinesTile
            key={index}
            {...tile}
            disabled={tile.status !== 'idle'}
            onClick={() => onClickTile?.(index)}
          />
        )
      })}
    </Box>
  )
})

export default MinesTiles
